import { useAppI18N, variantProvider } from '@/app'
import { AnyFunction, useCallback, useMemo } from '@codeleap/common'
import { Modal, Text, View } from '@/components'
import { APIClient, useSharedSettings } from '@/services'
import subDays from 'date-fns/subDays'
import { Hurdle } from '@/types'
import { hurdleItem } from '@/utils'
import { navigate } from 'gatsby'
import { useFilteredList } from '@/utils/hooks/useFilteredList'
import { Learn } from '@/features'

type ArchiveModalProps = {
  toggle: AnyFunction
  visible: boolean
  hurdle: Hurdle
}

const today = new Date()

export const HurdleArchiveModal = ({ toggle, visible, hurdle: currentHurdle }: ArchiveModalProps) => {
  const sharedSettings = useSharedSettings()

  const maxHurdleArchiveAge = sharedSettings.settings?.max_hurdle_archive_age ?? 20

  const after = useMemo(() => subDays(new Date(), maxHurdleArchiveAge), [maxHurdleArchiveAge])

  const hurdles = APIClient.Games.useHurdles({
    after,
    before: today,
    limit: maxHurdleArchiveAge
  })

  const data = useFilteredList(hurdles?.items ?? [], i => i.id === currentHurdle?.id)

  const goToGame = (id: Hurdle['id']) => {
    toggle(false)
    navigate(`/hurdle/game/${id}` as never)
  }

  const renderItem = useCallback((item: Hurdle, index: number) => {
    return (
      <Learn.HurdleCard
        key={item?.id + 'hurdle-archive'}
        module={{ ...hurdleItem, created_datetime: item.date }}
        hurdle={item}
        onPress={() => goToGame(item?.id)}
        showYear
      />
    )
  }, [])

  return (
    <Modal
      visible={visible}
      toggle={toggle}
      debugName={`Modal hurdle help`}
      title={null}
      variants={['boxPadding:4']}
      closable={true}
      styles={{
        header: styles.header,
        box: styles.box
      }}
    >
      <View variants={['column', 'gap:2']}>
        <View variants={['row', 'justifySpaceBetween', 'alignCenter']}>
          <Text text="Archive" variants={['h1', 'bold', 'color:neutral9']} />
          <Text text="Showing the last 20 days" variants={['p1', 'regular', 'color:neutral6']} />
        </View>

        <View variants={['column', 'gap:2']}>{data.map(renderItem)}</View>
      </View>
    </Modal>
  )
}

const MODAL_WIDTH = 600

const styles = variantProvider.createComponentStyle(
  theme => ({
    box: {
      width: theme.presets.calcWidth(theme.spacing.value(4)),
      maxWidth: MODAL_WIDTH,
      gap: theme.spacing.value(2)
    },
    header: {
      [theme.media.down('small')]: {
        paddingTop: theme.spacing.value(2)
      }
    }
  }),
  true
)
